import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
    isPublicLanguage,
    publicDocumentPath,
    publicReportApi,
    publicReportProblemOf,
    type PublicDocument,
    type PublicLanguage,
    type PublicReport,
    type PublicReportProblem,
    type PublicReportProblemKind,
    type PublicTenant,
} from '@/entities/publicReport';
import { reportPdfFilename } from '@/entities/report';

import { offerDownload } from './browser';

/**
 * De klantpagina haalt haar gegevens met het token uit de link. Geen sessie,
 * geen omgeving: de link zegt zelf bij welk bedrijf hij hoort.
 *
 * - De sleutel begint met `public`, dus de query gaat nooit mee in de bewaarde
 *   querycache (`QueryProvider`), en `gcTime: 0` ruimt hem op zodra niemand
 *   meer kijkt: een rapport van een koper hoort niet op een gedeeld apparaat
 *   achter te blijven.
 * - De toegangscode leeft alleen in dit geheugen, niet in de adresbalk en niet
 *   in opslag.
 * - De taal zit in de sleutel: kiest de koper een andere taal, dan komt de
 *   inhoud opnieuw op (`?lang=`), en blijft de oude pagina staan tot de nieuwe
 *   er is. De keuze staat in de adresbalk (`?taal=en`, zo ook in de link uit de
 *   mail) en in de browser (`localStorage`), zodat de pagina de volgende keer in
 *   dezelfde taal opent.
 * - Vertaalt de server nog (`translation.status: pending`), dan staat het
 *   Nederlandse rapport er met een korte laadregel, en vraagt de pagina elke
 *   anderhalve seconde of de vertaling klaar is; daarna haalt ze het rapport in
 *   de nieuwe taal op. Lukte het niet, dan blijft het Nederlands staan en zegt de
 *   pagina dat.
 */
export interface UsePublicReportResult {
    report: PublicReport | undefined;
    /**
     * Huisstijl voor de laad-, code- en foutpagina's: uit het rapport, of uit
     * het foutantwoord van een echte link (verlopen, ingetrokken, code nodig).
     * Eenmaal bekend blijft hij staan terwijl een ingevulde code wordt gecontroleerd.
     */
    tenant: PublicTenant | null;
    language: PublicLanguage;
    setLanguage: (language: PublicLanguage) => void;
    problem: PublicReportProblem | null;
    loading: boolean;
    /** Een ingevulde code wordt gecontroleerd. */
    checkingCode: boolean;
    submitAccessCode: (code: string) => void;
    /** Opnieuw proberen; na te veel foute codes begint de koper weer bij het codeveld. */
    retry: () => void;
    share: (email: string) => Promise<void>;
    ask: (message: string, email: string | null) => Promise<void>;
    /**
     * Verse foto- en downloadlinks: die zijn ondertekend en verlopen na een
     * kwartier, en wie het hele rapport leest, komt pas later bij een foto.
     * Hooguit eens per halve minuut.
     */
    refreshLinks: () => void;
    /**
     * Een downloadknop: haalt de PDF op en biedt hem aan. `false` als de link
     * ergens anders heen wijst; die volgt de browser dan zelf. Alleen op de
     * publieke pagina; de kantoorpreview haalt de PDF met de sessie.
     */
    openDocument?: (document: PublicDocument) => boolean;
    /** Welke knop nu ophaalt, en van welke de PDF niet kwam. */
    documentState?: PublicDocumentState;
    /** De gekozen taal wordt nog vertaald (`pending`), of dat lukte niet (`failed`). */
    translationState?: 'pending' | 'failed' | null;
}

export interface PublicDocumentState {
    busy: string | null;
    failed: string | null;
}

/** Een download die hierop stuit, zegt niets over de pdf maar over de link: de pagina toont het. */
const LINK_PROBLEMS: readonly PublicReportProblemKind[] = [
    'notFound',
    'expired',
    'revoked',
    'codeRequired',
    'codeInvalid',
    'tooManyAttempts',
];

/** Zo vaak haalt een pagina hoogstens verse links op, hoeveel foto's er ook tegelijk niet laden. */
const LINK_REFRESH_MS = 30_000;

/** Zo vaak vraagt de pagina of de vertaling klaar is, en hoe lang hooguit (zo'n anderhalve minuut). */
const TRANSLATION_POLL_MS = 1_500;
const TRANSLATION_POLL_MAX = 60;

/** Waar de browser de taalkeuze van de koper onthoudt. */
export const LANGUAGE_STORAGE_KEY = 'smartkeur.klantpagina.taal';

/** De taal in de adresbalk: `?taal=en`. */
export const LANGUAGE_PARAM = 'taal';

function storedLanguage(): PublicLanguage | null {
    try {
        const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

        return value !== null && isPublicLanguage(value) ? value : null;
    } catch {
        return null;
    }
}

function storeLanguage(language: PublicLanguage): void {
    try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
        /* Een browser die niets bewaart (privévenster), opent de volgende keer in het Nederlands. */
    }
}

/**
 * `refetch`, maar hoogstens eens per `LINK_REFRESH_MS`: tachtig foto's die
 * tegelijk verlopen zijn, vragen het rapport één keer opnieuw op.
 */
export function useLinkRefresh(refetch: () => Promise<unknown>): () => void {
    const last = useRef(0);

    return useCallback(() => {
        const now = Date.now();

        if (now - last.current < LINK_REFRESH_MS) {
            return;
        }

        last.current = now;
        void refetch();
    }, [refetch]);
}

export const publicReportKey = (
    token: string,
    language: PublicLanguage | null = null,
    accessCode: string | null = null,
) => ['public-report', token, language, accessCode] as const;

export function usePublicReport(token: string): UsePublicReportResult {
    const client = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const fromAddress = searchParams.get(LANGUAGE_PARAM);
    const [requested, setRequested] = useState<PublicLanguage | null>(() =>
        fromAddress !== null && isPublicLanguage(fromAddress) ? fromAddress : storedLanguage(),
    );
    const [accessCode, setAccessCode] = useState<string | null>(null);

    const query = useQuery({
        queryKey: publicReportKey(token, requested, accessCode),
        queryFn: () =>
            publicReportApi.load({
                token,
                ...(requested ? { language: requested } : {}),
                ...(accessCode ? { accessCode } : {}),
            }),
        retry: false,
        gcTime: 0,
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnReconnect: false,
        placeholderData: keepPreviousData,
        enabled: token !== '',
    });

    const problem = useMemo(
        () => (query.error ? publicReportProblemOf(query.error) : null),
        [query.error],
    );
    /* Een taal die dit bedrijf niet aanbiedt (de server vertaalt niet), valt terug op de inhoud. */
    const offered = query.data?.availableLanguages ?? null;
    const language =
        requested !== null && (offered === null || offered.includes(requested))
            ? requested
            : (query.data?.language ?? 'nl');

    const chooseLanguage = useCallback(
        (next: PublicLanguage) => {
            setRequested(next);
            storeLanguage(next);
            setSearchParams(
                (params) => {
                    const updated = new URLSearchParams(params);

                    if (next === 'nl') {
                        updated.delete(LANGUAGE_PARAM);
                    } else {
                        updated.set(LANGUAGE_PARAM, next);
                    }

                    return updated;
                },
                { replace: true, preventScrollReset: true },
            );
        },
        [setSearchParams],
    );

    /*
     * De server vertaalt nog: vraag tot hij klaar is of het opgeeft, en haal dan de
     * pagina opnieuw op - nu in de nieuwe taal, of met de melding dat het niet lukte.
     */
    const translation = query.data?.translation ?? null;
    const pendingLanguage =
        translation?.status === 'pending' && translation.language === requested
            ? translation.language
            : null;
    const translationStatus = useQuery({
        queryKey: ['public-report-translation', token, pendingLanguage, accessCode] as const,
        queryFn: () =>
            publicReportApi.translation({
                token,
                language: pendingLanguage ?? 'nl',
                ...(accessCode ? { accessCode } : {}),
            }),
        enabled: pendingLanguage !== null,
        retry: false,
        gcTime: 0,
        refetchInterval: (status) =>
            status.state.data?.status === 'pending' &&
            status.state.dataUpdateCount < TRANSLATION_POLL_MAX
                ? TRANSLATION_POLL_MS
                : false,
    });
    const settled =
        pendingLanguage !== null &&
        translationStatus.data !== undefined &&
        translationStatus.data?.status !== 'pending';

    const { refetch: refetchReport } = query;

    useEffect(() => {
        if (settled) {
            void refetchReport();
        }
    }, [settled, refetchReport]);

    const translationState =
        requested === null || requested === 'nl' || translation?.language !== requested
            ? null
            : translation.status === 'pending'
              ? 'pending'
              : translation.status === 'failed'
                ? 'failed'
                : null;

    /* Tijdens het controleren van een code is er even geen rapport en geen fout. */
    const [knownTenant, setKnownTenant] = useState<PublicTenant | null>(null);
    const seenTenant = query.data?.tenant ?? problem?.tenant ?? null;

    if (seenTenant !== null && seenTenant !== knownTenant) {
        setKnownTenant(seenTenant);
    }

    const share = useMutation({
        mutationFn: (email: string) => publicReportApi.share({ token, email, accessCode }),
        onSuccess: () => {
            void client.invalidateQueries({ queryKey: ['public-report', token] });
        },
    });

    const ask = useMutation({
        mutationFn: (input: { message: string; email: string | null }) =>
            publicReportApi.ask({ token, accessCode, ...input }),
    });

    const submitAccessCode = useCallback((code: string) => {
        setAccessCode(code.trim());
    }, []);

    const { refetch } = query;
    const refreshLinks = useLinkRefresh(refetch);

    /*
     * De PDF via de client in plaats van de link te volgen: kan de server hem nog
     * niet maken, dan zegt de pagina dat rustig bij de knop.
     */
    const [documentState, setDocumentState] = useState<PublicDocumentState>({
        busy: null,
        failed: null,
    });
    const fetchingDocument = useRef(false);
    const orderNumber = query.data?.orderNumber ?? '';
    const version = query.data?.fullReport?.cover.version ?? 1;

    const openDocument = useCallback(
        (document: PublicDocument): boolean => {
            const path = publicDocumentPath(document.url);

            if (path === null) {
                return false;
            }

            /* Een tweede tik terwijl de PDF nog komt, vraagt hem niet nog eens. */
            if (fetchingDocument.current) {
                return true;
            }

            fetchingDocument.current = true;
            setDocumentState({ busy: document.key, failed: null });

            void publicReportApi
                .document(path, accessCode)
                .then(
                    (blob) => {
                        offerDownload(blob, reportPdfFilename({ orderNumber, version }));
                        setDocumentState({ busy: null, failed: null });
                    },
                    (error: unknown) => {
                        /*
                         * Werkt de link zelf niet meer (verlopen, ingetrokken, code nodig),
                         * dan laat de pagina dat zien, zoals bij het openen. Anders is het
                         * de pdf die er nog niet is.
                         */
                        if (LINK_PROBLEMS.includes(publicReportProblemOf(error).kind)) {
                            setDocumentState({ busy: null, failed: null });
                            void refetch();

                            return;
                        }

                        setDocumentState({ busy: null, failed: document.key });
                    },
                )
                .finally(() => {
                    fetchingDocument.current = false;
                });

            return true;
        },
        [accessCode, orderNumber, version, refetch],
    );

    const retry = useCallback(() => {
        if (query.error && publicReportProblemOf(query.error).kind === 'tooManyAttempts') {
            setAccessCode(null);

            return;
        }

        void refetch();
    }, [query.error, refetch]);

    return {
        report: query.data,
        tenant: seenTenant ?? knownTenant,
        language,
        setLanguage: chooseLanguage,
        translationState,
        problem,
        loading: query.isPending,
        checkingCode: accessCode !== null && query.isFetching && query.data === undefined,
        submitAccessCode,
        retry,
        share: async (email) => {
            await share.mutateAsync(email);
        },
        ask: async (message, email) => {
            await ask.mutateAsync({ message, email });
        },
        refreshLinks,
        openDocument,
        documentState,
    };
}
